import { Button, Flex, Table, Tooltip, theme as antdTheme } from 'antd'
import { FileExcelOutlined, UserAddOutlined, WarningOutlined } from '@ant-design/icons'
import queryString from 'query-string'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import dayjs from 'dayjs'
import { useLocation } from 'react-router-dom'
import { useAsync } from '../../hooks'
import useExportExcelAsync from '../../hooks/useExportExcelAsync'
import useQueryParams from '../../hooks/useQueryParams'
import { formatDOB } from '../../lib/datetime'
import {
  useFetchPatients,
  usePatients,
  usePatientsPagination,
} from '../../redux/slices/patients'
import {
  useIsAdmin,
  useCurrentUserPracticeId,
  useCurrentUser,
} from '../../redux/slices/session'
import { toPath } from '../../routes'
import { ListError, PageHeader } from '../Common'
import AddNewItemButton from '../Common/AddNewItemButton'
import Filters, { FIELDS_OPTIONS } from '../Forms/Filters'
import api from '../../api'
import CircleCheckIcon from '../Common/CircleCheckIcon'
import DiseasesTags from '../Common/DiseasesTags'
import UnapprovedCount from './UnapprovedCount'
import PatientEntry from '../Common/PatientEntry'

const antTheme = antdTheme.getDesignToken()

const useStyles = createUseStyles({
  patientName: {
    minWidth: '15vw',
    display: 'flex',
  },
  ok: {
    color: antTheme.colorSuccess,
  },
  notOk: {
    color: antTheme.colorError,
  },
  exportContainer: {
    textAlign: 'center',
  },
  disabledRow: {
    background: '#fbfbfb',
    opacity: 0.5,
  },
})

const QUERY_PARAMS_SCHEMA = {
  page: Number,
  showInactive: Boolean,
  hasDeviceWarnings: Boolean,
}

const DEFAULT_FILTERS = {
  approved: FIELDS_OPTIONS.approved[1].id,
  enrolled: FIELDS_OPTIONS.enrolled[0].id,
  page: 1,
  paired: FIELDS_OPTIONS.paired[0].id,
  deviceModelManufacturer: FIELDS_OPTIONS.deviceModelManufacturer[0].id,
  monitoring: FIELDS_OPTIONS.monitoringTypes[0].id,
}

export default function PatientsList() {
  const theme = useTheme()
  const classes = useStyles({ theme })
  const isAdmin = useIsAdmin()
  const location = useLocation()
  const user = useCurrentUser()
  const isProvider = user?.position === 'provider'
  const [hasInteracted, setHasInteracted] = useState(false)
  sessionStorage.setItem('patientReturnTo', `${location.pathname}${location.search}`)

  const [practiceOfficeLocations, setPracticeOfficeLocations] = useState([])
  const [conditions, setConditions] = useQueryParams({
    initialValues: {
      ...DEFAULT_FILTERS,
      medicalProviderId: isProvider && !hasInteracted ? user?.id : undefined,
    },
    schema: QUERY_PARAMS_SCHEMA,
  })
  const patients = usePatients()
  const userPracticeId = useCurrentUserPracticeId()
  const { pageSize: defaultPageSize, total } = usePatientsPagination()

  // Extract individual condition values to prevent infinite loop
  const {
    page,
    pageSize,
    approved,
    practiceId,
    showInactive,
    hasDeviceWarnings,
    query,
    deviceTypeId,
    deviceModelManufacturer,
    medicalProviderId,
    enrolled,
    paired,
    monitoring,
    birthDate,
    officeLocationId,
  } = conditions

  // Memoize the conditions object to prevent infinite loop
  // The object only changes when individual condition values actually change
  const memoizedConditions = useMemo(
    () => ({
      page,
      pageSize,
      approved,
      practiceId,
      showInactive,
      hasDeviceWarnings,
      query,
      deviceTypeId,
      deviceModelManufacturer,
      medicalProviderId,
      enrolled,
      paired,
      monitoring,
      birthDate,
      officeLocationId,
    }),
    [
      page,
      pageSize,
      approved,
      practiceId,
      showInactive,
      hasDeviceWarnings,
      query,
      deviceTypeId,
      deviceModelManufacturer,
      medicalProviderId,
      enrolled,
      paired,
      monitoring,
      birthDate,
      officeLocationId,
    ],
  )

  const fetchPatientsCallback = useFetchPatients(memoizedConditions, true)

  const {
    pending: loading,
    error,
    clearError,
  } = useAsync(fetchPatientsCallback, true)

  const handlePageChange = useCallback(
    (p, pS) => {
      setConditions({ ...conditions, page: p, pageSize: pS })
    },
    [conditions, setConditions],
  )

  useEffect(() => {
    const practiceToFetch = userPracticeId || conditions.practiceId
    if (practiceToFetch) {
      api.practices.getPracticeOfficeLocations(practiceToFetch).then(data => {
        setPracticeOfficeLocations(data)
        if (
          !data.find(
            loc =>
              String(loc.id) === String(conditions.officeLocationId),
          )
        ) {
          setConditions({ ...conditions, officeLocationId: undefined })
        }
      })
    } else {
      setConditions({ ...conditions, officeLocationId: undefined })
    }
  }, [conditions, conditions.practiceId, setConditions, userPracticeId])


  const excelExportUrl = useMemo(
    () =>
      queryString.stringifyUrl({
        url: '/patients/exports',
        query: {
          approved: conditions.approved,
          practice_id: conditions.practiceId,
          device_type_id: conditions.deviceTypeId,
          medical_provider_id: conditions.medicalProviderId,
          device_model_manufacturer: conditions.deviceModelManufacturer,
          enrolled: conditions.enrolled,
          paired: conditions.paired,
          show_inactive: conditions.showInactive || undefined,
          has_device_warnings: conditions.hasDeviceWarnings || undefined,
          monitoring: conditions.monitoring,
          birth_date: conditions.birthDate,
          q: conditions.query,
          office_location_id: conditions.officeLocationId,
        },
      }),
    [
      conditions.approved,
      conditions.deviceModelManufacturer,
      conditions.deviceTypeId,
      conditions.medicalProviderId,
      conditions.enrolled,
      conditions.paired,
      conditions.practiceId,
      conditions.query,
      conditions.showInactive,
      conditions.hasDeviceWarnings,
      conditions.monitoring,
      conditions.birthDate,
      conditions.officeLocationId,
    ],
  )

  const formatBirthDate = bd =>
    bd ? dayjs(bd).format('YYYY-MM-DD') : undefined

  const handleSubmit = useCallback(
    (payload) => {
      setHasInteracted(true)
      const filters = {
        approved: payload.approved,
        deviceTypeId: payload.deviceTypeId,
        deviceModelManufacturer: payload.deviceModelManufacturer,
        medicalProviderId: payload.medicalProviderId,
        birthDate: formatBirthDate(payload.birthDate),
        page: 1,
        practiceId: payload.practiceId,
        showInactive: payload.showInactive || undefined,
        hasDeviceWarnings: payload.hasDeviceWarnings || undefined,
        query: payload.query,
        monitoring: payload.monitoring,
        officeLocationId: payload.officeLocationId?.key || payload.officeLocationId,
      }
      setConditions(!isAdmin ? filters : { ...filters, enrolled: payload.enrolled, paired: payload.paired })
    },
    [isAdmin, setConditions],
  )

  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Patient',
        dataIndex: ['patient', 'lastName'],
        render(text, patient) {
          return <PatientEntry patient={patient} />
        },
      },
      {
        title: 'MRN',
        dataIndex: ['practiceMrn'],
      },
      {
        title: 'DOB',
        dataIndex: ['birthDate'],
        key: 'birthDate',
        render: formatDOB,
      },
      {
        title: 'Stroke Prevention',
        key: 'diseases',
        render(text, patient) {
          const patientSummary = {
            firstName: patient.firstName,
            lastName: patient.lastName,
            atrialFibrilationFlutterDisease:
              patient.atrialFibrilationFlutterDisease,
            anticoagulatedDisease: patient.anticoagulatedDisease,
          }
          return <DiseasesTags patientSummary={patientSummary} />
        },
      },
      {
        title: 'Warnings',
        dataIndex: ['hasDeviceWarnings'],
        key: 'hasDeviceWarnings',
        render(_, patient) {
          return (
            patient.hasDeviceWarnings
              ? (
                <Flex justify="center">
                    <Tooltip title={patient.deviceWarnings.map((warning) => <li>
                      <div>{warning}</div>
                    </li>)}>
                      <WarningOutlined style={{ color: theme.brandDangerMid }} />
                    </Tooltip>
                </Flex>
              )
              : <span />
          )
        },
      },
      {
        title: 'Device Type',
        dataIndex: ['currentDevice', 'modelType'],
        key: 'modelType',
      },
      {
        title: 'Device Company',
        dataIndex: ['currentDevice', 'modelManufacturer'],
        key: 'modelManufacturer',
      },
      {
        title: 'Serial Number',
        dataIndex: ['currentDevice', 'serialNumber'],
        key: 'serialNumber',
      },
    ]
    return !isAdmin
      ? cols
      : [
          ...cols,
          {
            title: 'Enrolled',
            dataIndex: ['enrolled'],
            key: 'enrolled',
            render(_, patient) {
              return (
                <Flex justify="center">
                  <CircleCheckIcon checked={patient.enrolled} />
                </Flex>
              )
            },
          },
          {
            title: 'Paired',
            dataIndex: ['paired'],
            key: 'paired',
            render(_, patient) {
              return (
                <Flex justify="center">
                  <CircleCheckIcon checked={patient.paired} />
                </Flex>
              )
            },
          },
        ]
  }, [isAdmin, theme.brandDangerMid])

  const rowClassName = useCallback(
    patient => (patient.active ? undefined : classes.disabledRow),
    [classes.disabledRow],
  )

  const [exportingExcel, exportExcel] = useExportExcelAsync(excelExportUrl)

  const currentFilterValues = useMemo(() => {
    if (userPracticeId) {
      return {
        practiceId: userPracticeId,
        ...conditions,
      }
    }
    return conditions
  }, [conditions, userPracticeId])



  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
      <PageHeader
        title="Patients"
        extra={
          <AddNewItemButton
            itemName="patient"
            path={toPath.patients.new()}
            icon={<UserAddOutlined />}
          />
        }
      />
      {isAdmin && conditions.approved !== 'false' && (
        <div style={{ marginBottom: '8px' }}>
          <UnapprovedCount />
        </div>
      )}
      <Filters
        currentValues={currentFilterValues}
        defaultValues={DEFAULT_FILTERS}
        showApprovalStatus
        showDeviceType
        showMedicalProvider
        showDeviceManufacturer
        showEnrollment={isAdmin}
        showInactiveCheck
        showPairing={isAdmin}
        showPractice={isAdmin}
        showMonitoringType
        showBirthDate
        showSearch
        showDeviceWarningsCheck
        onSubmit={handleSubmit}
        placeholders={{
          query: 'Search by name, serial number or practice MRN',
        }}
        showOfficeLocation={
          (conditions.practiceId || userPracticeId) &&
          practiceOfficeLocations.length > 0
        }
      />

      <Table
        size="middle"
        dataSource={patients}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          current: conditions.page || 1,
          pageSize: pageSize || defaultPageSize,
          onChange: handlePageChange,
          total,
        }}
        rowClassName={rowClassName}
      />

      {Boolean(patients.length) && (
        <div className={classes.exportContainer}>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon={<FileExcelOutlined />}
          >
            Export this list to Excel
          </Button>
        </div>
      )}

    </div>
  )
}
